/*
 * GET /v2/sales_task_list/my_leads?period=7|14|21|35[&ops_uuid=] (PROD-9977).
 * Row lists inside a present block are normalised to arrays; absent blocks stay null.
 */
import { get_api as getApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import type { LeadsPeriod, MedianCounts, MyLeadsData, TeamShiftSummary } from './types';

const URL_PATH = '/v2/sales_task_list/my_leads';

interface Envelope<T> {
  data?: { data?: T };
}

interface LeadsApiError {
  response?: {
    status?: number;
    data?: { reason?: string; error_code?: string; message?: string };
  };
}

const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const NO_COUNTS: MedianCounts = {
  trials_booked: 0,
  trials_joined_rate: 0,
  converted: 0,
  conversion_rate: 0,
  days_to_pay: 0
};

export const leadsQuery = (period: LeadsPeriod, opsUuid?: string): string => {
  const params = new URLSearchParams({ period: String(period) });
  if (opsUuid) params.set('ops_uuid', opsUuid);
  return params.toString();
};

// Null blocks (non-active roster) stay null; a present block always gets an array.
export const normalizeLeads = (d: MyLeadsData): MyLeadsData => ({
  ...d,
  shift: d.shift ? { ...d.shift, hours: arr(d.shift.hours) } : null,
  leads_joined: d.leads_joined ? { ...d.leads_joined, rows: arr(d.leads_joined.rows) } : null,
  converted: d.converted ? { ...d.converted, rows: arr(d.converted.rows) } : null,
  // A payload without counts shows no medians rather than an unlabelled one.
  medians: d.medians ? { ...d.medians, counts: { ...NO_COUNTS, ...d.medians.counts } } : null,
  ...(d.l1 ? { l1: { ...d.l1, rows: arr(d.l1.rows) } } : null),
  ...(d.l2 ? { l2: { ...d.l2, rows: arr(d.l2.rows) } } : null)
});

export const fetchMyLeads = (period: LeadsPeriod, opsUuid?: string): Promise<MyLeadsData> =>
  getApi(getApiUrl(`${URL_PATH}?${leadsQuery(period, opsUuid)}`), true).then(
    (res: Envelope<MyLeadsData>) => {
      const d = res?.data?.data;
      if (!d || !d.rep) throw new Error('Empty my_leads response');
      return normalizeLeads(d);
    }
  );

export const leadsErrorStatus = (e: unknown): number | undefined =>
  (e as LeadsApiError | null)?.response?.status;

export const leadsErrorReason = (e: unknown): string => {
  const data = (e as LeadsApiError | null)?.response?.data;
  return data?.reason || data?.error_code || data?.message || '';
};

const TEAM_SHIFT_PATH = '/v2/sales_task_list/team_shift_summary';

export const fetchTeamShiftSummary = (): Promise<TeamShiftSummary> =>
  getApi(getApiUrl(TEAM_SHIFT_PATH), true).then((res: Envelope<TeamShiftSummary>) => {
    const d = res?.data?.data;
    if (!d) throw new Error('Empty team_shift_summary response');
    return { ...d, levels: arr(d.levels) };
  });
